import { createFileRoute, useNavigate, redirect } from "@tanstack/react-router";
import {
  getStoredAuth,
  authApi,
  setToken,
  setRefreshToken,
  ensureFreshAuth,
  clearToken,
  isAuthExpiredError,
} from "@/lib/api";
import { useState, useEffect } from "react";
import { Building2, Camera, DollarSign, Check, ArrowRight, MapPin, Locate, Trash2, Plus, Users, X, Loader2, RotateCcw } from "lucide-react";
import { Map, Marker } from "pigeon-maps";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FACILITIES, LOCATIONS, FACILITY_PHOTO_LABELS, DEFAULT_ROOM_TYPE_PRESETS, type RoomType, type FacilityType } from "@/data/venues";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { venuesApi, uploadApi, galleryApi } from "@/lib/api";
import imageCompression from "browser-image-compression";

export const Route = createFileRoute("/_host/list-your-property")({
  head: () => ({
    meta: [
      { title: "Become a Host — OyoBooking" },
      {
        name: "description",
        content:
          "Earn money by hosting your hotel, suite, event centre, or open space on OyoBooking. Reach thousands of local guests.",
      },
    ],
    links: [{ rel: "canonical", href: "https://oyobooking.ng/list-your-property" }],
  }),
  component: ListYourPropertyPage,
  beforeLoad: ({ location }) => {
    if (typeof window === "undefined") return;
    const auth = getStoredAuth();
    if (!auth) throw redirect({ to: "/login", search: { redirect: location.pathname } });
    const HOST_ROLES = [
      "venue_manager", "hotel_owner", "event_centre_owner", "open_space_owner",
      "resort_owner", "hotel_event_owner", "apartment_owner", "conference_hall_owner",
    ] as const;
    if (!auth.role || !(HOST_ROLES as readonly string[]).includes(auth.role)) {
      throw redirect({ to: "/" });
    }
  },
});

const ALL_STEPS = [
  { n: 1, label: "Property", icon: Building2 },
  { n: 2, label: "Photos", icon: Camera },
  { n: 3, label: "Details & Pricing", icon: DollarSign },
] as const;

const HOTEL_TYPES: FacilityType[] = ["Hotel"];
const HAS_STEP3_TYPES: FacilityType[] = ["Hotel", "Resort", "Apartment", "Conference Hall", "Event Center", "Open Space"];
const LISTING_DRAFT_KEY = "oyo_listing_draft";
const BACKEND_PHOTO_LIMIT_BYTES = 10 * 1024 * 1024;
const PHOTO_COMPRESSION_TARGET_MB = 8.5;
const PHOTO_COMPRESSION_TARGET_BYTES = PHOTO_COMPRESSION_TARGET_MB * 1024 * 1024;
const PHOTO_MAX_DIMENSION = 2400;

type UploadStatus = "queued" | "compressing" | "uploading" | "uploaded" | "failed";

type UploadItem = {
  id: string;
  previewUrl: string;
  remoteUrl?: string;
  status: UploadStatus;
  error?: string;
  file?: File;
};

const getMinimumListingPrice = (facility: string) =>
  ["Conference Hall", "Event Center", "Open Space"].includes(facility) ? 50_000 : 5_000;

const getListingPriceLabel = (facility: string) =>
  ["Conference Hall", "Event Center", "Open Space"].includes(facility)
    ? "hire price"
    : facility === "Apartment"
      ? "booking price"
      : "nightly price";

const cleanCurrencyInput = (value: string) => value.replace(/[^\d]/g, "");
const parseCurrencyInput = (value: unknown) => Number(cleanCurrencyInput(String(value ?? "")) || 0);
const parsePositiveIntInput = (value: unknown) => Number(String(value ?? "").replace(/[^\d]/g, "") || 0);

const fileSizeMb = (bytes: number) => (bytes / (1024 * 1024)).toFixed(1);

const preparePhotoForUpload = async (file: File): Promise<File> => {
  if (file.size <= PHOTO_COMPRESSION_TARGET_BYTES) return file;

  let compressed: File;
  try {
    const output = await imageCompression(file, {
      maxSizeMB: PHOTO_COMPRESSION_TARGET_MB,
      maxWidthOrHeight: PHOTO_MAX_DIMENSION,
      useWebWorker: true,
      initialQuality: 0.86,
      fileType: "image/jpeg",
    });

    compressed = output instanceof File
      ? output
      : new File(
          [output],
          file.name.replace(/\.[^.]+$/, ".jpg"),
          { type: output.type || "image/jpeg", lastModified: Date.now() },
        );
  } catch {
    throw new Error(`We couldn't compress this ${fileSizeMb(file.size)} MB photo. Please choose a smaller image.`);
  }

  if (compressed.size > BACKEND_PHOTO_LIMIT_BYTES) {
    throw new Error(
      `This photo is still ${fileSizeMb(compressed.size)} MB after compression. Please choose a smaller image.`,
    );
  }

  return compressed;
};

const CITY_COORDINATES: Record<string, [number, number]> = {
  Ibadan: [7.3775, 3.9470],
  "Ibadan North": [7.4200, 3.9170],
  "Ibadan South-East": [7.3600, 3.9300],
  "Ibadan South-West": [7.3500, 3.8800],
  "Ibadan North-East": [7.4300, 3.9500],
  "Ibadan North-West": [7.4100, 3.8600],
  Ogbomoso: [8.1227, 4.2436],
  "Ogbomoso North": [8.1400, 4.2600],
  "Ogbomoso South": [8.1000, 4.2200],
  Oyo: [7.8500, 3.9333],
  "Oyo West": [7.8400, 3.9100],
  "Oyo East": [7.8600, 3.9500],
  Iseyin: [7.9717, 3.5901],
  Saki: [8.6676, 3.3958],
  "Saki West": [8.6500, 3.3800],
  "Saki East": [8.6800, 3.4100],
  Kisi: [9.0825, 3.8496],
  Igboho: [8.8354, 3.7533],
  Eruwa: [7.5316, 3.4221],
  Lanlate: [7.4833, 3.4167],
  "Igbo-Ora": [7.4333, 3.2833],
  Fiditi: [7.7167, 3.9167],
  Lalupon: [7.4667, 4.0500],
  Ayete: [7.5667, 3.3833],
  "Ago-Are": [8.4833, 3.4500],
  Tede: [8.5500, 3.4167],
  Igbeti: [8.7500, 4.1333],
  Okeho: [8.0333, 3.3500],
  Ilora: [7.8000, 3.8833],
  Awe: [7.7667, 4.1667],
};

function ListYourPropertyPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [step, setStep] = useState(1);
  const [isMounted, setIsMounted] = useState(false);
  const [showMapSelector, setShowMapSelector] = useState(false);
  const [form, setForm] = useState({
    name: "",
    facility: "",
    location: "",
    description: "",
    capacity: 4,
    latitude: undefined as number | undefined,
    longitude: undefined as number | undefined,
  });
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [showPresetPicker, setShowPresetPicker] = useState(false);
  const [customRoomName, setCustomRoomName] = useState("");
  const [uploadedPhotos, setUploadedPhotos] = useState<string[]>([]);
  const [photoItems, setPhotoItems] = useState<Record<string, UploadItem[]>>({});
  const [roomPhotoItems, setRoomPhotoItems] = useState<Record<string, UploadItem[]>>({});
  const [draftReady, setDraftReady] = useState(false);

  const PHOTO_LABELS = (FACILITY_PHOTO_LABELS[form.facility] || FACILITY_PHOTO_LABELS["Hotel"]);

  const makeUploadId = () =>
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const flattenPhotoItems = (items: Record<string, UploadItem[]>) => Object.values(items).flat();

  const getUploadedVenueUrls = (items = photoItems) =>
    flattenPhotoItems(items)
      .filter((item) => item.status === "uploaded" && item.remoteUrl)
      .map((item) => item.remoteUrl as string);

  const getUploadedRoomUrls = (roomId: string, items = roomPhotoItems) =>
    (items[roomId] || [])
      .filter((item) => item.status === "uploaded" && item.remoteUrl)
      .map((item) => item.remoteUrl as string);

  const statusLabel = (status: UploadStatus) => {
    if (status === "compressing") return "Compressing";
    if (status === "uploading") return "Uploading";
    if (status === "uploaded") return "Uploaded";
    if (status === "failed") return "Failed";
    return "Queued";
  };

  const statusClass = (status: UploadStatus) => {
    if (status === "uploaded") return "bg-emerald-600 text-white";
    if (status === "failed") return "bg-destructive text-destructive-foreground";
    if (status === "compressing") return "bg-amber-500 text-white";
    if (status === "uploading") return "bg-foreground/80 text-background";
    return "bg-muted text-muted-foreground";
  };

  const uploadVenuePhotoItem = async (sectionKey: string, itemId: string, file?: File) => {
    if (!file) throw new Error("Please reselect this photo before retrying.");
    setPhotoItems(prev => ({
      ...prev,
      [sectionKey]: (prev[sectionKey] || []).map(item =>
        item.id === itemId ? { ...item, status: "compressing", error: undefined } : item
      ),
    }));

    try {
      const uploadFile = await preparePhotoForUpload(file);
      setPhotoItems(prev => ({
        ...prev,
        [sectionKey]: (prev[sectionKey] || []).map(item =>
          item.id === itemId ? { ...item, status: "uploading", file: uploadFile } : item
        ),
      }));
      const result = await uploadApi.image(uploadFile);
      setPhotoItems(prev => ({
        ...prev,
        [sectionKey]: (prev[sectionKey] || []).map(item =>
          item.id === itemId ? { ...item, status: "uploaded", remoteUrl: result.url, error: undefined } : item
        ),
      }));
      return result.url;
    } catch (err) {
      const message = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Upload failed.";
      setPhotoItems(prev => ({
        ...prev,
        [sectionKey]: (prev[sectionKey] || []).map(item =>
          item.id === itemId ? { ...item, status: "failed", error: message } : item
        ),
      }));
      throw err;
    }
  };

  const uploadRoomPhotoItem = async (roomId: string, itemId: string, file?: File) => {
    if (!file) throw new Error("Please reselect this photo before retrying.");
    setRoomPhotoItems(prev => ({
      ...prev,
      [roomId]: (prev[roomId] || []).map(item =>
        item.id === itemId ? { ...item, status: "compressing", error: undefined } : item
      ),
    }));

    try {
      const uploadFile = await preparePhotoForUpload(file);
      setRoomPhotoItems(prev => ({
        ...prev,
        [roomId]: (prev[roomId] || []).map(item =>
          item.id === itemId ? { ...item, status: "uploading", file: uploadFile } : item
        ),
      }));
      const result = await uploadApi.image(uploadFile);
      setRoomPhotoItems(prev => ({
        ...prev,
        [roomId]: (prev[roomId] || []).map(item =>
          item.id === itemId ? { ...item, status: "uploaded", remoteUrl: result.url, error: undefined } : item
        ),
      }));
      return result.url;
    } catch (err) {
      const message = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Upload failed.";
      setRoomPhotoItems(prev => ({
        ...prev,
        [roomId]: (prev[roomId] || []).map(item =>
          item.id === itemId ? { ...item, status: "failed", error: message } : item
        ),
      }));
      throw err;
    }
  };

  const saveDraft = () => {
    if (typeof window === "undefined") return;
    const sanitizedRoomTypes = roomTypes.map((room) => ({ ...room, images: [] }));
    const serializedPhotoItems = Object.fromEntries(
      Object.entries(photoItems).map(([key, items]) => [
        key,
        items
          .filter(item => item.status === "uploaded" && item.remoteUrl)
          .map(item => ({
            id: item.id,
            previewUrl: item.remoteUrl,
            remoteUrl: item.remoteUrl,
            status: item.status,
          })),
      ]),
    );
    const serializedRoomPhotoItems = Object.fromEntries(
      Object.entries(roomPhotoItems).map(([key, items]) => [
        key,
        items
          .filter(item => item.status === "uploaded" && item.remoteUrl)
          .map(item => ({
            id: item.id,
            previewUrl: item.remoteUrl,
            remoteUrl: item.remoteUrl,
            status: item.status,
          })),
      ]),
    );
    localStorage.setItem(
      LISTING_DRAFT_KEY,
      JSON.stringify({
        step,
        form,
        roomTypes: sanitizedRoomTypes,
        photoItems: serializedPhotoItems,
        roomPhotoItems: serializedRoomPhotoItems,
        savedAt: new Date().toISOString(),
      }),
    );
  };

  const handleSessionExpired = () => {
    saveDraft();
    clearToken();
    toast.error("Your session expired. Please sign in again to continue your listing. Your draft has been saved.");
    navigate({ to: "/login", search: { redirect: "/list-your-property" } });
  };

  const hasAuthUploadFailure = (results: PromiseSettledResult<unknown>[]) =>
    results.some((result) => result.status === "rejected" && isAuthExpiredError(result.reason));

  const firstUploadErrorMessage = (results: PromiseSettledResult<unknown>[]) => {
    const rejected = results.find((result) => result.status === "rejected");
    if (!rejected || rejected.status !== "rejected") return null;
    return rejected.reason instanceof Error ? rejected.reason.message.replace(/^HTTP \d+:\s*/i, "") : null;
  };

  const handleLabelledPhotoUpload = (key: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    const items = files.map((file) => ({
      id: makeUploadId(),
      previewUrl: URL.createObjectURL(file),
      status: "queued" as UploadStatus,
      file,
    }));
    const urls = items.map((item) => item.previewUrl);
    setPhotoItems(prev => ({ ...prev, [key]: [...(prev[key] || []), ...items] }));
    setUploadedPhotos(prev => [...prev, ...urls]);
    toast.success(`Added ${files.length} photo(s)!`);
    e.target.value = "";
    void Promise.allSettled(items.map((item) => uploadVenuePhotoItem(key, item.id, item.file)));
  };

  useEffect(() => {
    setIsMounted(true);
    try {
      const raw = localStorage.getItem(LISTING_DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw) as {
          step?: number;
          form?: typeof form;
          roomTypes?: RoomType[];
          photoItems?: Record<string, UploadItem[]>;
          roomPhotoItems?: Record<string, UploadItem[]>;
        };
        if (draft.form) setForm(prev => ({ ...prev, ...draft.form }));
        if (Array.isArray(draft.roomTypes)) setRoomTypes(draft.roomTypes);
        if (draft.photoItems) {
          setPhotoItems(draft.photoItems);
          setUploadedPhotos(flattenPhotoItems(draft.photoItems).map(item => item.remoteUrl || item.previewUrl));
        }
        if (draft.roomPhotoItems) setRoomPhotoItems(draft.roomPhotoItems);
        if (draft.step && draft.step > 1) setStep(Math.min(draft.step, 3));
        toast.info("Your saved listing draft has been restored. Please reselect any photos before submitting.");
      }
    } catch {
      localStorage.removeItem(LISTING_DRAFT_KEY);
    } finally {
      setDraftReady(true);
    }
  }, []);

  useEffect(() => {
    if (!draftReady) return;
    saveDraft();
  }, [draftReady, form, roomTypes, step, photoItems, roomPhotoItems]);

  // Synchronize city coordinates if location dropdown changes while selector is active
  useEffect(() => {
    if (showMapSelector && form.location) {
      const defaultCoords = CITY_COORDINATES[form.location];
      if (defaultCoords) {
        setForm(prev => ({ ...prev, latitude: defaultCoords[0], longitude: defaultCoords[1] }));
      }
    }
  }, [form.location, showMapSelector]);

  const pinCurrentLocation = () => {
    if (typeof window === "undefined" || !navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    toast.loading("Retrieving your exact coordinates...", { id: "geo-toast" });

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setForm(prev => ({ ...prev, latitude, longitude }));
        toast.success("Coordinates successfully loaded & pinned!", { id: "geo-toast" });
      },
      (err) => {
        console.error(err);
        toast.error("Access denied. Please check your browser's location settings.", { id: "geo-toast" });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleNextStep = () => {
    if (step === 1) {
      if (!form.name.trim()) {
        toast.error("Property name is compulsory");
        return;
      }
      if (!form.facility) {
        toast.error("Facility type is compulsory");
        return;
      }
      if (!form.location) {
        toast.error("Location city is compulsory");
        return;
      }
      if (!form.description.trim()) {
        toast.error("Property description is compulsory");
        return;
      }
    } else if (step === 2) {
      if (uploadedPhotos.length === 0) {
        toast.error("Please upload at least one photo (compulsory)");
        return;
      }
    }
    const hasStep3 = HAS_STEP3_TYPES.includes(form.facility as FacilityType);
    const maxStep = hasStep3 ? 3 : 2;
    if (step === 2 && !hasStep3) {
      submit();
      return;
    }
    setStep((s) => Math.min(maxStep, s + 1));
  };

  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !form.facility || !form.location || !form.description.trim()) {
      toast.error("Please fill in all property information fields (compulsory)");
      setStep(1);
      return;
    }

    const isHotelOnly = HOTEL_TYPES.includes(form.facility as FacilityType);

    if (isHotelOnly && roomTypes.length === 0) {
      toast.error("Please add at least one room type");
      setStep(3);
      return;
    }

    if (isHotelOnly) {
      const invalidRoom = roomTypes.find(rt => !rt.price || rt.price < 5000);
      if (invalidRoom) {
        toast.error(`Please set a valid price (min ₦5,000) for "${invalidRoom.name}"`);
        setStep(3);
        return;
      }
    }

    const hirePrice = parseCurrencyInput((form as any).hirePrice);
    if (!isHotelOnly) {
      const minPrice = getMinimumListingPrice(form.facility);
      if (!hirePrice || hirePrice < minPrice) {
        toast.error(`Please set a valid ${getListingPriceLabel(form.facility)} of at least ₦${minPrice.toLocaleString()}`);
        setStep(HAS_STEP3_TYPES.includes(form.facility as FacilityType) ? 3 : 1);
        return;
      }
    }

    if (form.facility === "Apartment") {
      const capacity = parsePositiveIntInput(form.capacity);
      if (!capacity || capacity < 1) {
        toast.error("Please enter the maximum number of guests");
        setStep(3);
        return;
      }
    }

    const lowestPrice = isHotelOnly && roomTypes.length > 0
      ? Math.min(...roomTypes.map(r => r.price))
      : hirePrice;

    const hasFreshAuth = await ensureFreshAuth();
    if (!hasFreshAuth) {
      handleSessionExpired();
      return;
    }

    setSubmitting(true);
    try {
      /* ── 1. Upload all venue photos ── */
      const venueItemsToUpload = Object.entries(photoItems).flatMap(([sectionKey, items]) =>
        items
          .filter(item => item.status !== "uploaded" && item.file)
          .map(item => ({ sectionKey, item })),
      );
      const existingVenueUrls = getUploadedVenueUrls();
      let uploadedVenueUrls: string[] = [];

      if (venueItemsToUpload.length > 0) {
        toast.loading(`Uploading ${venueItemsToUpload.length} pending photo(s)...`, { id: "upload-toast" });
        const uploadResults = await Promise.allSettled(
          venueItemsToUpload.map(({ sectionKey, item }) => uploadVenuePhotoItem(sectionKey, item.id, item.file)),
        );
        if (hasAuthUploadFailure(uploadResults)) {
          toast.dismiss("upload-toast");
          handleSessionExpired();
          return;
        }
        uploadedVenueUrls = uploadResults
          .filter((r): r is PromiseFulfilledResult<string> => r.status === "fulfilled")
          .map(r => r.value);

        const failed = uploadResults.filter(r => r.status === "rejected").length;
        toast.dismiss("upload-toast");
        if (existingVenueUrls.length + uploadedVenueUrls.length === 0) {
          const uploadMessage = firstUploadErrorMessage(uploadResults);
          toast.error(uploadMessage || "None of the photos could be uploaded. Please check the image type, file size, and your connection.");
          setSubmitting(false);
          return;
        }
        if (failed > 0) {
          toast.warning(`${failed} photo(s) failed to upload. We'll continue with the ${existingVenueUrls.length + uploadedVenueUrls.length} that uploaded successfully.`);
        }
      }

      const imageUrls = Array.from(new Set([...existingVenueUrls, ...uploadedVenueUrls]));
      if (imageUrls.length === 0) {
        toast.error("Please upload at least one photo before submitting.");
        setSubmitting(false);
        return;
      }

      /* ── 1b. Upload room type photos ── */
      const roomImageUrlsById: Record<string, string[]> = {};
      const totalPendingRoomPhotos = Object.values(roomPhotoItems)
        .flat()
        .filter(item => item.status !== "uploaded" && item.file)
        .length;
      if (totalPendingRoomPhotos > 0) {
        toast.loading(`Uploading ${totalPendingRoomPhotos} pending room photo(s)...`, { id: "room-upload-toast" });
      }
      for (const rt of roomTypes) {
        const roomItems = roomPhotoItems[rt.id] || [];
        const existingRoomUrls = getUploadedRoomUrls(rt.id);
        const pendingRoomItems = roomItems.filter(item => item.status !== "uploaded" && item.file);
        let uploadedRoomUrls: string[] = [];
        if (pendingRoomItems.length > 0) {
          const results = await Promise.allSettled(
            pendingRoomItems.map(item => uploadRoomPhotoItem(rt.id, item.id, item.file)),
          );
          if (hasAuthUploadFailure(results)) {
            toast.dismiss("room-upload-toast");
            handleSessionExpired();
            return;
          }
          uploadedRoomUrls = results
            .filter((r): r is PromiseFulfilledResult<string> => r.status === "fulfilled")
            .map(r => r.value);
          const failedRoomUploads = results.filter(r => r.status === "rejected").length;
          if (failedRoomUploads > 0) {
            toast.warning(`${failedRoomUploads} photo(s) for ${rt.name} failed to upload. You can add them later from your dashboard.`);
          }
        }
        roomImageUrlsById[rt.id] = Array.from(new Set([...existingRoomUrls, ...uploadedRoomUrls]));
      }
      toast.dismiss("room-upload-toast");

      const newVenue = await venuesApi.create({
        name: form.name.trim(),
        type: form.facility,
        description: form.description.trim(),
        address: form.location,
        location: form.location,
        heroImage: imageUrls[0],
        basePrice: lowestPrice,
        latitude: form.latitude,
        longitude: form.longitude,
        roomTypes: isHotelOnly
          ? roomTypes.map(r => ({
              name: r.name,
              pricePerDay: r.price,
              capacity: r.maxGuests,
              quantity: Math.max(1, r.quantity || 1),
              description: r.description,
              images: roomImageUrlsById[r.id] || [],
            }))
          : undefined,
      });

      const galleryUrls = imageUrls.slice(1);
      if (galleryUrls.length > 0 && newVenue.id) {
        const galleryResults = await Promise.allSettled(
          galleryUrls.map(url => galleryApi.add(newVenue.id, { imageUrl: url }))
        );
        const galleryFailed = galleryResults.filter(r => r.status === "rejected").length;
        if (galleryFailed > 0) {
          toast.error(`${galleryFailed} gallery photo(s) failed to save. You can add them later from your dashboard.`);
        }
      }

      try {
        const refreshed = await authApi.refresh();
        setToken(refreshed.accessToken);
        setRefreshToken(refreshed.refreshToken);
      } catch { /* role update visible after next login if refresh fails */ }
      toast.success("Property listed successfully! You can manage it from your dashboard.");
      localStorage.removeItem(LISTING_DRAFT_KEY);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      navigate({ to: "/admin/listings" as any });
    } catch (err) {
      if (isAuthExpiredError(err)) {
        handleSessionExpired();
        return;
      }
      const msg = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Failed to list property. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Hero banner */}
      <section className="bg-gradient-warm border-b border-border/50">
        <div className="mx-auto max-w-4xl px-4 py-12 text-center sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Become a host</p>
          <h1 className="mt-3 font-display text-3xl font-bold sm:text-4xl lg:text-5xl">List your property in minutes.</h1>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Reach thousands of guests across Oyo State. We handle bookings, payments and reviews — you focus on hospitality.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
            {["No upfront fees", "Free photography", "24/7 support", "Paid in 48hrs"].map((p) => (
              <span key={p} className="flex items-center gap-1.5"><Check className="size-4 text-success" /> {p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-border/60 bg-card p-6 shadow-elegant sm:p-8 lg:p-10">
              {(() => {
                const hasStep3 = HAS_STEP3_TYPES.includes(form.facility as FacilityType);
                const step3Label = form.facility === "Hotel" ? "Room Types & Pricing"
                  : form.facility === "Resort" ? "Resort Facilities"
                  : form.facility === "Apartment" ? "Apartment & Pricing"
                  : ["Conference Hall", "Event Center"].includes(form.facility) ? "Hall & Pricing"
                  : form.facility === "Open Space" ? "Space & Pricing"
                  : "Details & Pricing";
                const STEPS = (hasStep3 ? ALL_STEPS : ALL_STEPS.filter(s => s.n <= 2)).map(s =>
                  s.n === 3 ? { ...s, label: step3Label } : s
                );
                return (
                  <>
                    <ol className="flex items-center justify-between">
                      {STEPS.map((s, i) => {
                        const active = step === s.n;
                        const done = step > s.n;
                        const Icon = done ? Check : s.icon;
                        return (
                          <li key={s.n} className="flex flex-1 items-center">
                            <div className="flex flex-col items-center gap-2">
                              <div className={`grid size-10 place-items-center rounded-full border-2 ${done ? "border-primary bg-primary text-primary-foreground" : active ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
                                <Icon className="size-4" />
                              </div>
                              <span className="text-[11px] font-medium">{s.label}</span>
                            </div>
                            {i < STEPS.length - 1 && <div className={`mx-2 mb-6 h-0.5 flex-1 ${step > s.n ? "bg-primary" : "bg-border"}`} />}
                          </li>
                        );
                      })}
                    </ol>
                  </>
                );
              })()}

              <div className="mt-8 space-y-4">
                {step === 1 && (
                  <>
                    <div className="space-y-2">
                      <Label>Property name</Label>
                      <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Sunrise Boutique Hotel" />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Facility type</Label>
                        <Select value={form.facility} onValueChange={(v) => setForm({ ...form, facility: v })}>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{FACILITIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Location</Label>
                        <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Tell guests what makes your place special…" />
                    </div>

                    {/* Optional Map Location Selection integration */}
                    <div className="space-y-3 pt-2">
                      {!showMapSelector ? (
                        <button
                          type="button"
                          onClick={() => {
                            setShowMapSelector(true);
                            // If location is set, default map center to city coordinates, else default to Ibadan
                            const defaultCoords = CITY_COORDINATES[form.location] || CITY_COORDINATES["Ibadan"];
                            setForm(prev => ({ ...prev, latitude: defaultCoords[0], longitude: defaultCoords[1] }));
                          }}
                          className="flex items-center gap-3 rounded-xl border border-border/50 bg-muted/20 hover:bg-muted/40 p-4 w-full text-left text-sm font-semibold transition-all group"
                        >
                          <div className="size-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                            <MapPin className="size-4" />
                          </div>
                          <div>
                            <p className="text-foreground">Pin property location on map (Optional)</p>
                            <p className="text-[11px] font-normal text-muted-foreground">Allows guests to get directions directly to your property</p>
                          </div>
                        </button>
                      ) : (
                        <div className="rounded-2xl border border-border/60 bg-muted/10 p-4 space-y-4 animate-in fade-in slide-in-from-top-2 duration-350">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <MapPin className="size-4 text-primary animate-bounce" />
                              <span className="text-[11px] font-bold text-foreground uppercase tracking-wider">Map Pin Location</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setShowMapSelector(false);
                                setForm(prev => ({ ...prev, latitude: undefined, longitude: undefined }));
                              }}
                              className="text-xs font-semibold text-destructive hover:underline flex items-center gap-1"
                            >
                              <Trash2 className="size-3" /> Remove Location
                            </button>
                          </div>

                          {/* Map Selection Canvas */}
                          <div className="h-56 w-full overflow-hidden rounded-xl border border-border bg-muted relative">
                            {isMounted && form.latitude && form.longitude ? (
                              <Map
                                center={[form.latitude, form.longitude]}
                                zoom={14}
                                onClick={({ latLng }) => {
                                  setForm(prev => ({ ...prev, latitude: latLng[0], longitude: latLng[1] }));
                                }}
                                dprs={[1, 2]}
                              >
                                <Marker
                                  anchor={[form.latitude, form.longitude]}
                                  onClick={() => { }}
                                >
                                  <div className="relative -translate-x-1/2 -translate-y-full block pointer-events-none">
                                    <div className="absolute -inset-1.5 rounded-full bg-primary/25 animate-ping opacity-75" />
                                    <div className="relative flex items-center justify-center size-7 rounded-full bg-primary text-primary-foreground border-2 border-background shadow-md">
                                      <MapPin className="size-3.5 fill-primary-foreground text-primary-foreground" />
                                    </div>
                                  </div>
                                </Marker>
                              </Map>
                            ) : (
                              <div className="grid size-full place-items-center text-xs text-muted-foreground animate-pulse">
                                Loading location selector...
                              </div>
                            )}

                            {/* Tap Instruction */}
                            <div className="absolute bottom-2 left-2 z-40 bg-foreground/90 backdrop-blur-sm text-background text-[10px] py-1 px-2.5 rounded-md font-medium shadow pointer-events-none">
                              Click map to fine-tune your location pin
                            </div>
                          </div>

                          {/* Pin Button & Coordinates */}
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-card border border-border/40 rounded-xl p-3">
                            <button
                              type="button"
                              onClick={pinCurrentLocation}
                              className="flex items-center justify-center gap-1.5 rounded-lg bg-primary hover:bg-primary/95 text-primary-foreground py-2 px-3 text-xs font-semibold shadow-soft hover:shadow transition-all"
                            >
                              <Locate className="size-3.5 animate-pulse" /> Pin My Current Location
                            </button>

                            <div className="flex items-center gap-3 text-xs font-medium text-muted-foreground sm:text-right">
                              <div>
                                <span className="block text-[9px] text-muted-foreground/80 uppercase tracking-wider">Latitude</span>
                                <span className="text-foreground font-mono text-[11px]">{form.latitude?.toFixed(5) || "—"}</span>
                              </div>
                              <div className="h-6 w-px bg-border" />
                              <div>
                                <span className="block text-[9px] text-muted-foreground/80 uppercase tracking-wider">Longitude</span>
                                <span className="text-foreground font-mono text-[11px]">{form.longitude?.toFixed(5) || "—"}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
                {step === 2 && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-display text-lg font-semibold">Show off your property</h3>
                      <p className="text-sm text-muted-foreground mt-1">Upload photos for each area. At least 1 exterior photo is required.</p>
                    </div>

                    {/* Labelled photo sections */}
                    <div className="space-y-4">
                      {PHOTO_LABELS.map((section) => {
                        const photos = photoItems[section.key] || [];
                        const isRequired = section.required;
                        return (
                          <div key={section.key} className="rounded-2xl border border-border/60 bg-accent/10 p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <span className="text-xl">{section.emoji}</span>
                                <div>
                                  <span className="text-sm font-semibold">{section.label}</span>
                                  {isRequired && (
                                    <span className="ml-2 text-[10px] bg-primary/10 text-primary py-0.5 px-2 rounded-full font-semibold">Required</span>
                                  )}
                                  <p className="text-[11px] text-muted-foreground">{section.hint}</p>
                                </div>
                              </div>
                              <span className="text-xs text-muted-foreground font-medium">{photos.length} photo{photos.length !== 1 ? "s" : ""}</span>
                            </div>

                            <div className="flex flex-wrap gap-2 items-start">
                              {photos.map((item, imgIdx) => (
                                <div key={item.id} className="relative size-20 rounded-xl overflow-hidden border border-border group/img shrink-0">
                                  <img src={item.previewUrl} alt={`${section.label} ${imgIdx + 1}`} className="size-full object-cover" />
                                  <span className={`absolute left-1 top-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${statusClass(item.status)}`}>
                                    {(item.status === "compressing" || item.status === "uploading") && <Loader2 className="mr-1 inline size-2.5 animate-spin" />}
                                    {statusLabel(item.status)}
                                  </span>
                                  {item.status === "failed" && (
                                    <button
                                      type="button"
                                      onClick={() => void uploadVenuePhotoItem(section.key, item.id, item.file)}
                                      className="absolute bottom-1 left-1 right-1 flex items-center justify-center gap-1 rounded-md bg-background/95 px-1.5 py-1 text-[10px] font-semibold text-foreground shadow"
                                      title={item.error || "Retry upload"}
                                    >
                                      <RotateCcw className="size-3" /> Retry
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const removed = item.previewUrl;
                                      setPhotoItems(prev => ({
                                        ...prev,
                                        [section.key]: (prev[section.key] || []).filter((p) => p.id !== item.id),
                                      }));
                                      setUploadedPhotos(prev => prev.filter(p => p !== removed));
                                    }}
                                    className="absolute inset-0 flex items-center justify-center bg-black/50 text-white opacity-0 group-hover/img:opacity-100 transition-opacity"
                                    aria-label="Remove photo"
                                  >
                                    <Trash2 className="size-3.5" />
                                  </button>
                                </div>
                              ))}
                              <label className="flex flex-col items-center justify-center size-20 rounded-xl border-2 border-dashed border-border/60 hover:border-primary/40 bg-background hover:bg-primary/5 cursor-pointer transition-all shrink-0">
                                <Camera className="size-5 text-muted-foreground" />
                                <span className="text-[10px] text-muted-foreground mt-1">Add</span>
                                <input
                                  type="file"
                                  multiple
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => handleLabelledPhotoUpload(section.key, e)}
                                />
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Summary */}
                    <div className="rounded-xl bg-accent/30 border border-border/40 p-3 flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Total photos uploaded</span>
                      <span className="font-semibold text-foreground">{uploadedPhotos.length} photo{uploadedPhotos.length !== 1 ? "s" : ""}</span>
                    </div>

                    <p className="text-xs text-muted-foreground italic">
                      Tip: bright, wide-angle photos get 30% more bookings.
                    </p>
                  </div>
                )}
                {step === 3 && HAS_STEP3_TYPES.includes(form.facility as FacilityType) && HOTEL_TYPES.includes(form.facility as FacilityType) && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-display text-lg font-semibold">What type of rooms do you have?</h3>
                      <p className="text-sm text-muted-foreground mt-1">Tap the ones that match your property. You can also create your own.</p>
                    </div>

                    {/* Visual room type picker grid — always visible */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {DEFAULT_ROOM_TYPE_PRESETS.map((preset) => {
                        const isAdded = roomTypes.some(rt => rt.name === preset.name);
                        return (
                          <button
                            key={preset.name}
                            type="button"
                            onClick={() => {
                              if (isAdded) {
                                setRoomTypes(prev => prev.filter(rt => rt.name !== preset.name));
                              } else {
                                const id = `${preset.name.toLowerCase().replace(/\s+/g, "-")}-${Date.now()}`;
                                setRoomTypes(prev => [...prev, { id, name: preset.name, price: 0, description: preset.description, maxGuests: preset.maxGuests, quantity: 1, images: [] }]);
                                toast.success(`${preset.name} added!`);
                              }
                            }}
                            className={`relative flex flex-col items-center gap-2 rounded-2xl border-2 p-4 text-center transition-all ${
                              isAdded
                                ? "border-primary bg-primary/5 shadow-sm"
                                : "border-border/50 bg-background hover:border-primary/30 hover:bg-accent/20"
                            }`}
                          >
                            {isAdded && (
                              <div className="absolute top-2 right-2 size-5 rounded-full bg-primary flex items-center justify-center">
                                <Check className="size-3 text-primary-foreground" />
                              </div>
                            )}
                            <span className="text-2xl">{preset.icon}</span>
                            <span className="text-sm font-semibold leading-tight">{preset.name}</span>
                            <span className="text-[11px] text-muted-foreground leading-tight">{preset.shortLabel}</span>
                          </button>
                        );
                      })}

                      {/* Create custom card */}
                      <button
                        type="button"
                        onClick={() => setShowPresetPicker(true)}
                        className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border/60 bg-muted/10 p-4 text-center hover:border-primary/40 hover:bg-primary/5 transition-all"
                      >
                        <span className="text-2xl">✏️</span>
                        <span className="text-sm font-semibold">Create your own</span>
                        <span className="text-[11px] text-muted-foreground">Name it yourself</span>
                      </button>
                    </div>

                    {/* Custom room name input — shown when "Create your own" is tapped */}
                    {showPresetPicker && (
                      <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold">Give your room a name</p>
                          <button type="button" onClick={() => setShowPresetPicker(false)} className="text-muted-foreground hover:text-foreground">
                            <X className="size-4" />
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            value={customRoomName}
                            onChange={(e) => setCustomRoomName(e.target.value)}
                            placeholder="e.g. Penthouse, Royal Suite, VIP Lounge…"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            disabled={!customRoomName.trim()}
                            onClick={() => {
                              if (!customRoomName.trim()) return;
                              const id = `${customRoomName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${Date.now()}`;
                              setRoomTypes(prev => [...prev, { id, name: customRoomName.trim(), price: 0, description: "", maxGuests: 2, quantity: 1, images: [] }]);
                              toast.success(`${customRoomName.trim()} added!`);
                              setCustomRoomName("");
                              setShowPresetPicker(false);
                            }}
                          >
                            Add
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Selected room types — set price, guests, photos */}
                    {roomTypes.length > 0 && (
                      <div className="space-y-4">
                        <p className="text-sm font-semibold text-foreground">Now set the price for each room you picked:</p>
                        {roomTypes.map((rt, idx) => (
                          <div key={rt.id} className="rounded-2xl border border-border/60 bg-card p-4 space-y-3 relative group">
                            <button
                              type="button"
                              onClick={() => {
                                setRoomTypes(prev => prev.filter((_, i) => i !== idx));
                                setRoomPhotoItems(prev => {
                                  const next = { ...prev };
                                  delete next[rt.id];
                                  return next;
                                });
                              }}
                              className="absolute top-3 right-3 p-1.5 rounded-lg bg-muted/50 hover:bg-destructive hover:text-white text-muted-foreground transition-colors"
                              aria-label="Remove"
                            >
                              <X className="size-3.5" />
                            </button>
                            <div className="flex items-center gap-3">
                              <span className="text-xl">{DEFAULT_ROOM_TYPE_PRESETS.find(p => p.name === rt.name)?.icon || "🏠"}</span>
                              <div>
                                <span className="font-semibold text-foreground">{rt.name}</span>
                                <p className="text-xs text-muted-foreground">{rt.description || "Custom room type"}</p>
                              </div>
                            </div>
                            <div className="grid gap-3 sm:grid-cols-3">
                              <div className="space-y-1.5">
                                <Label className="text-xs font-medium">Price per night (₦)</Label>
                                <Input
                                  type="number"
                                  min={5000}
                                  step={5000}
                                  value={rt.price || ""}
                                  placeholder="e.g. 25000"
                                  onChange={(e) => {
                                    const val = Number(e.target.value);
                                    setRoomTypes(prev => prev.map((r, i) => i === idx ? { ...r, price: val } : r));
                                  }}
                                />
                                {rt.price > 0 && (
                                  <p className="text-[10px] text-muted-foreground">You earn ₦{Math.round(rt.price * 0.9).toLocaleString()} after 10% fee</p>
                                )}
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-xs font-medium">How many guests?</Label>
                                <Input
                                  type="number"
                                  min={1}
                                  max={500}
                                  value={rt.maxGuests}
                                  onChange={(e) => {
                                    const val = Number(e.target.value);
                                    setRoomTypes(prev => prev.map((r, i) => i === idx ? { ...r, maxGuests: val } : r));
                                  }}
                                />
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-xs font-medium">Number of rooms</Label>
                                <Input
                                  type="number"
                                  min={1}
                                  max={500}
                                  value={rt.quantity || 1}
                                  onChange={(e) => {
                                    const val = Math.max(1, Number(e.target.value) || 1);
                                    setRoomTypes(prev => prev.map((r, i) => i === idx ? { ...r, quantity: val } : r));
                                  }}
                                />
                              </div>
                            </div>

                            {/* Labelled room photo sections */}
                            <div className="space-y-3 pt-2 border-t border-border/40">
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Room photos</p>
                              {[
                                { key: "bed", label: "Room / Bed", emoji: "🛏️", required: true },
                                { key: "bathroom", label: "Bathroom", emoji: "🚿", required: false },
                                { key: "parlor", label: "Parlor / Sitting Area", emoji: "🛋️", required: false },
                                { key: "view", label: "Window View", emoji: "🪟", required: false },
                              ].map((photoLabel) => {
                                const roomPhotoKey = `${rt.id}_${photoLabel.key}`;
                                const sectionPhotos = (rt.images as any)?.[photoLabel.key] as string[] || [];
                                return (
                                  <div key={photoLabel.key} className="flex items-center gap-3 rounded-xl border border-border/40 bg-accent/10 p-2.5">
                                    <span className="text-lg shrink-0">{photoLabel.emoji}</span>
                                    <div className="flex-1 min-w-0">
                                      <span className="text-xs font-semibold">{photoLabel.label}</span>
                                      {photoLabel.required ? (
                                        <span className="ml-1.5 text-[9px] bg-primary/10 text-primary py-0.5 px-1.5 rounded-full font-semibold">Required</span>
                                      ) : (
                                        <span className="ml-1.5 text-[9px] text-muted-foreground">Optional</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                      {rt.images.filter((_: string, ii: number) => {
                                        // Show images tagged with this label key (simplified: by index range)
                                        return false;
                                      }).length > 0 && <span className="text-[10px] text-muted-foreground">uploaded</span>}
                                      <label className="flex items-center justify-center size-9 rounded-lg border border-dashed border-border/60 hover:border-primary/40 bg-background hover:bg-primary/5 cursor-pointer transition-all">
                                        <Camera className="size-3.5 text-muted-foreground" />
                                        <input
                                          type="file"
                                          multiple
                                          accept="image/*"
                                          className="hidden"
                                          onChange={(e) => {
                                            if (!e.target.files) return;
                                            const files = Array.from(e.target.files);
                                            const items = files.map((file) => ({
                                              id: makeUploadId(),
                                              previewUrl: URL.createObjectURL(file),
                                              status: "queued" as UploadStatus,
                                              file,
                                            }));
                                            const urls = items.map(item => item.previewUrl);
                                            setRoomTypes(prev => prev.map((r, i) =>
                                              i === idx ? { ...r, images: [...r.images, ...urls] } : r
                                            ));
                                            setRoomPhotoItems(prev => ({
                                              ...prev,
                                              [rt.id]: [...(prev[rt.id] || []), ...items],
                                            }));
                                            toast.success(`Added ${urls.length} ${photoLabel.label} photo(s)`);
                                            e.target.value = "";
                                            void Promise.allSettled(items.map((item) => uploadRoomPhotoItem(rt.id, item.id, item.file)));
                                          }}
                                        />
                                      </label>
                                    </div>
                                  </div>
                                );
                              })}
                              {/* Show all uploaded room images */}
                              {(roomPhotoItems[rt.id] || []).length > 0 && (
                                <div className="flex flex-wrap gap-2 pt-1">
                                  {(roomPhotoItems[rt.id] || []).map((item, imgIdx: number) => (
                                    <div key={item.id} className="relative size-16 rounded-lg overflow-hidden border border-border group/img shrink-0">
                                      <img src={item.previewUrl} alt={`${rt.name} ${imgIdx + 1}`} className="size-full object-cover" />
                                      <span className={`absolute left-1 top-1 rounded-full px-1 py-0.5 text-[8px] font-semibold ${statusClass(item.status)}`}>
                                        {(item.status === "compressing" || item.status === "uploading") && <Loader2 className="mr-0.5 inline size-2 animate-spin" />}
                                        {statusLabel(item.status)}
                                      </span>
                                      {item.status === "failed" && (
                                        <button
                                          type="button"
                                          onClick={() => void uploadRoomPhotoItem(rt.id, item.id, item.file)}
                                          className="absolute bottom-1 left-1 right-1 flex items-center justify-center gap-1 rounded bg-background/95 px-1 py-0.5 text-[9px] font-semibold text-foreground shadow"
                                          title={item.error || "Retry upload"}
                                        >
                                          <RotateCcw className="size-2.5" /> Retry
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setRoomPhotoItems(prev => ({
                                            ...prev,
                                            [rt.id]: (prev[rt.id] || []).filter((p) => p.id !== item.id),
                                          }));
                                          setRoomTypes(prev => prev.map((r, i) =>
                                            i === idx ? { ...r, images: r.images.filter((_: string, j: number) => j !== imgIdx) } : r
                                          ));
                                        }}
                                        className="absolute inset-0 flex items-center justify-center bg-black/50 text-white opacity-0 group-hover/img:opacity-100 transition-opacity"
                                        aria-label="Remove"
                                      >
                                          <Trash2 className="size-2.5" />
                                      </button>
                                    </div>
                                  ))}
                                  <p className="text-[10px] text-muted-foreground self-end">{(roomPhotoItems[rt.id] || []).length} photo{(roomPhotoItems[rt.id] || []).length !== 1 ? "s" : ""}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Summary bar */}
                    {roomTypes.length > 0 && (
                      <div className="rounded-2xl bg-accent/30 border border-border/40 p-4 flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{roomTypes.length} room type{roomTypes.length !== 1 ? "s" : ""} selected</span>
                        <span className="font-semibold text-foreground">
                          {roomTypes.some(r => r.price > 0)
                            ? `From ₦${Math.min(...roomTypes.filter(r => r.price > 0).map(r => r.price)).toLocaleString()}/night`
                            : "Set prices above ☝️"}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Apartment Step 3: pricing and details */}
                {step === 3 && form.facility === "Apartment" && (
                  <div className="space-y-5">
                    <div>
                      <h3 className="font-display text-lg font-semibold">Apartment Details & Pricing</h3>
                      <p className="text-sm text-muted-foreground mt-1">Set your booking price and the number of guests your apartment can host.</p>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium">Booking Price (NGN)</Label>
                        <Input
                          type="text"
                          inputMode="numeric"
                          min={5000}
                          placeholder="e.g. 250000"
                          value={(form as any).hirePrice || ""}
                          onChange={e => setForm(prev => ({ ...prev, hirePrice: cleanCurrencyInput(e.target.value) } as any))}
                        />
                        {parseCurrencyInput((form as any).hirePrice) > 0 && <p className="text-[10px] text-muted-foreground">You earn NGN {Math.round(parseCurrencyInput((form as any).hirePrice) * 0.9).toLocaleString()} after fee</p>}
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium">Max Guests</Label>
                        <Input
                          type="number"
                          min={1}
                          max={100}
                          value={form.capacity === undefined || form.capacity === null ? "" : String(form.capacity)}
                          onChange={e => {
                            const value = e.target.value.replace(/[^\d]/g, "");
                            setForm(prev => ({ ...prev, capacity: value === "" ? ("" as any) : Number(value) }));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Resort Step 3: facilities checklist */}
                {step === 3 && form.facility === "Resort" && (
                  <div className="space-y-5">
                    <div>
                      <h3 className="font-display text-lg font-semibold">Resort Details & Pricing</h3>
                      <p className="text-sm text-muted-foreground mt-1">Tick all that apply. All are optional — the more you add the better!</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Starting Nightly Price (NGN)</Label>
                      <Input
                        type="number"
                        min={5000}
                        step={5000}
                        placeholder="e.g. 65000"
                        value={(form as any).hirePrice || ""}
                        onChange={e => setForm(prev => ({ ...prev, hirePrice: cleanCurrencyInput(e.target.value) } as any))}
                      />
                        {parseCurrencyInput((form as any).hirePrice) > 0 && <p className="text-[10px] text-muted-foreground">You earn NGN {Math.round(parseCurrencyInput((form as any).hirePrice) * 0.9).toLocaleString()} after fee</p>}
                    </div>
                    {[
                      { emoji: "🏊", label: "Swimming Pool(s)", key: "pool" },
                      { emoji: "🧖", label: "Spa & Thermal Area", key: "spa" },
                      { emoji: "🏋️", label: "Gym / Fitness Centre", key: "gym" },
                      { emoji: "🍽️", label: "Restaurants & Bars", key: "dining" },
                      { emoji: "🎾", label: "Sports Courts (Tennis, Volleyball etc.)", key: "sports" },
                      { emoji: "🚤", label: "Water Sports (Kayak, Scuba, Paddle)", key: "watersports" },
                      { emoji: "⛳", label: "Golf Course / Mini Golf", key: "golf" },
                      { emoji: "👨‍👩‍👧", label: "Kids' Club / Play Area", key: "kids" },
                      { emoji: "🎶", label: "Nightclub / Live Entertainment", key: "nightlife" },
                      { emoji: "💼", label: "Conference / Business Centre", key: "conference" },
                    ].map(fac => {
                      const checked = ((form as any).facilities || []).includes(fac.key);
                      return (
                        <button
                          key={fac.key}
                          type="button"
                          onClick={() => {
                            const cur: string[] = (form as any).facilities || [];
                            setForm(prev => ({ ...prev, facilities: checked ? cur.filter((k: string) => k !== fac.key) : [...cur, fac.key] } as any));
                          }}
                          className={`w-full flex items-center gap-3 rounded-xl border-2 p-3 text-left transition-all ${checked ? "border-primary bg-primary/5" : "border-border/50 bg-background hover:border-primary/30"}`}
                        >
                          <span className="text-xl">{fac.emoji}</span>
                          <span className="text-sm font-medium flex-1">{fac.label}</span>
                          <span className={`size-5 rounded-full border-2 flex items-center justify-center shrink-0 ${checked ? "border-primary bg-primary" : "border-border"}`}>
                            {checked && <Check className="size-3 text-primary-foreground" />}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Conference Hall & Event Center Step 3: hall capacity + pricing */}
                {step === 3 && ["Conference Hall", "Event Center"].includes(form.facility) && (
                  <div className="space-y-5">
                    <div>
                      <h3 className="font-display text-lg font-semibold">Hall Details & Pricing</h3>
                      <p className="text-sm text-muted-foreground mt-1">Set your hall capacity and pricing for different event types.</p>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium">Hall Capacity (max guests)</Label>
                        <Input type="number" min={10} step={10} placeholder="e.g. 500"
                          value={(form as any).hallCapacity || ""}
                          onChange={e => setForm(prev => ({ ...prev, hallCapacity: Number(e.target.value) } as any))} />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium">Base Hire Price (₦ per event)</Label>
                        <Input type="number" min={50000} step={50000} placeholder="e.g. 500000"
                          value={(form as any).hirePrice || ""}
                          onChange={e => setForm(prev => ({ ...prev, hirePrice: cleanCurrencyInput(e.target.value) } as any))} />
                        {parseCurrencyInput((form as any).hirePrice) > 0 && <p className="text-[10px] text-muted-foreground">You earn NGN {Math.round(parseCurrencyInput((form as any).hirePrice) * 0.9).toLocaleString()} after fee</p>}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <Label className="text-xs font-medium">What event types do you host?</Label>
                      {["Wedding / Reception", "Corporate Event", "Birthday Party", "Conference / Seminar", "Concert / Show", "Graduation", "Religious Programme"].map(evType => {
                        const checked = ((form as any).eventTypes || []).includes(evType);
                        return (
                          <button key={evType} type="button"
                            onClick={() => {
                              const cur: string[] = (form as any).eventTypes || [];
                              setForm(prev => ({ ...prev, eventTypes: checked ? cur.filter((t: string) => t !== evType) : [...cur, evType] } as any));
                            }}
                            className={`w-full flex items-center gap-3 rounded-xl border-2 p-3 text-left transition-all text-sm font-medium ${checked ? "border-primary bg-primary/5" : "border-border/50 bg-background hover:border-primary/30"}`}
                          >
                            <span className={`size-5 rounded-full border-2 flex items-center justify-center shrink-0 ${checked ? "border-primary bg-primary" : "border-border"}`}>
                              {checked && <Check className="size-3 text-primary-foreground" />}
                            </span>
                            {evType}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Open Space Step 3: capacity + pricing */}
                {step === 3 && form.facility === "Open Space" && (
                  <div className="space-y-5">
                    <div>
                      <h3 className="font-display text-lg font-semibold">Space Details & Pricing</h3>
                      <p className="text-sm text-muted-foreground mt-1">Tell guests how many people your space can hold and how much to hire it.</p>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium">Max Capacity (guests)</Label>
                        <Input type="number" min={10} step={10} placeholder="e.g. 300"
                          value={(form as any).hallCapacity || ""}
                          onChange={e => setForm(prev => ({ ...prev, hallCapacity: Number(e.target.value) } as any))} />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium">Hire Price (₦ per event)</Label>
                        <Input type="number" min={50000} step={50000} placeholder="e.g. 300000"
                          value={(form as any).hirePrice || ""}
                          onChange={e => setForm(prev => ({ ...prev, hirePrice: cleanCurrencyInput(e.target.value) } as any))} />
                        {parseCurrencyInput((form as any).hirePrice) > 0 && <p className="text-[10px] text-muted-foreground">You earn NGN {Math.round(parseCurrencyInput((form as any).hirePrice) * 0.9).toLocaleString()} after fee</p>}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <Label className="text-xs font-medium">What type of space is it?</Label>
                      {["Garden / Lawn", "Rooftop", "Waterfront", "Beach / Riverside", "Poolside", "Courtyard", "Parking Lot / Open Ground"].map(sType => {
                        const checked = ((form as any).spaceTypes || []).includes(sType);
                        return (
                          <button key={sType} type="button"
                            onClick={() => {
                              const cur: string[] = (form as any).spaceTypes || [];
                              setForm(prev => ({ ...prev, spaceTypes: checked ? cur.filter((t: string) => t !== sType) : [...cur, sType] } as any));
                            }}
                            className={`w-full flex items-center gap-3 rounded-xl border-2 p-3 text-left transition-all text-sm font-medium ${checked ? "border-primary bg-primary/5" : "border-border/50 bg-background hover:border-primary/30"}`}
                          >
                            <span className={`size-5 rounded-full border-2 flex items-center justify-center shrink-0 ${checked ? "border-primary bg-primary" : "border-border"}`}>
                              {checked && <Check className="size-3 text-primary-foreground" />}
                            </span>
                            {sType}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-2">
                  <Button variant="ghost" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}>Back</Button>
                  {(() => {
                    const hasStep3 = HAS_STEP3_TYPES.includes(form.facility as FacilityType);
                    const lastStep = hasStep3 ? 3 : 2;
                    return step < lastStep ? (
                      <Button onClick={handleNextStep}>
                        {step === 2 && !hasStep3 ? "Submit listing" : "Continue"} <ArrowRight className="size-4" />
                      </Button>
                    ) : (
                      <Button onClick={submit} disabled={submitting}>{submitting ? "Submitting…" : "Submit listing"}</Button>
                    );
                  })()}
                </div>
              </div>
        </div>
      </section>
    </>
  );
}


